import {
  type CSSProperties,
  type ReactNode,
  ChangeEvent,
  FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import AppShell from "../components/AppShell";
import PreviewModal from "../components/PreviewModal";
import { mockStores } from "../data/mockStores";
import {
  fetchStoreGroups,
  saveDraft,
  updateDraft,
  sendDraft,
  createCampaign,
  uploadStoreAttachment,
} from "../lib/emailApi";
import {
  collectAudienceStoreIds,
  uniqueCountries,
  ownershipOptions,
  uniqueFranchiseGroups,
  uniqueRecipientEmails,
  uniqueRegions,
} from "../lib/storeGrouping";
import type {
  CreateCampaignDto,
  MessageType,
  StoreEmailDto,
  StoreRegionGroupDto,
  TemplateLayout,
} from "../types/campaign";
import {
  type AudienceSelections,
  type OwnershipType,
  type StoreRecord,
} from "../types/store";

// ── Helpers ─────────────────────────────────────────────────────────────────

function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1_048_576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1_048_576).toFixed(1)} MB`;
}

function storeMatchesSearch(store: StoreRecord, q: string) {
  const n = q.trim().toLowerCase();
  if (!n) return true;
  return [
    store.displayName,
    store.storeNumber,
    store.city,
    store.state,
    store.country,
    store.region,
    store.ownershipType,
    store.franchiseGroup ?? "",
    store.emails.join(" "),
  ]
    .join(" ")
    .toLowerCase()
    .includes(n);
}

function toggleValue<T>(arr: T[], v: T): T[] {
  return arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];
}

function apiStoresToRecords(
  stores: StoreEmailDto[],
  regionGroups: StoreRegionGroupDto[],
): StoreRecord[] {
  return stores.map((s) => {
    const region =
      regionGroups.find((group) =>
        group.stores.some((store) => store.storeNumber === s.storeNumber),
      )?.areaName ?? s.areaName ?? "Unknown";

    return {
      id: String(s.storeNumber),
      storeNumber: String(s.storeNumber),
      displayName: s.formalName,
      city: s.address1 ?? "",
      state: s.address2 ?? "",
      country: "US" as const,
      region,
      ownershipType: (s.operatedBy === 1 ? "Corporate" : "Franchise") as OwnershipType,
      franchiseGroup: null,
      emails: s.email ? [s.email] : [],
    };
  });
}

// ── HTML body editor (contenteditable) ──────────────────────────────────────

const TEMPLATE_VARS = [
  { key: "storeName", label: "Store name" },
  { key: "storeNumber", label: "Store #" },
  { key: "ownerEmail", label: "Owner email" },
  { key: "address", label: "Address" },
  { key: "corporateName", label: "Corporate name" },
  { key: "fullName", label: "Full name" },
] as const;

interface HtmlEditorProps {
  value: string;
  onChange: (html: string) => void;
}

function HtmlEditor({ value, onChange }: HtmlEditorProps) {
  const ref = useRef<HTMLDivElement>(null);
  const lastHtml = useRef(value);
  const isComposing = useRef(false);

  useEffect(() => {
    if (ref.current && value !== lastHtml.current) {
      ref.current.innerHTML = value;
      lastHtml.current = value;
    }
  }, [value]);

  function push() {
    if (ref.current) {
      lastHtml.current = ref.current.innerHTML;
      onChange(ref.current.innerHTML);
    }
  }

  function execCmd(cmd: string, val?: string) {
    ref.current?.focus();
    document.execCommand(cmd, false, val ?? undefined);
    push();
  }

  function insertVar(varKey: string) {
    ref.current?.focus();
    document.execCommand("insertText", false, `{{${varKey}}}`);
    push();
  }

  return (
    <div className="html-editor">
      <div className="html-editor__toolbar">
        <button
          type="button"
          className="html-editor__btn"
          title="Bold"
          onMouseDown={(e) => { e.preventDefault(); execCmd("bold"); }}
        >
          <b>B</b>
        </button>
        <button
          type="button"
          className="html-editor__btn"
          title="Italic"
          onMouseDown={(e) => { e.preventDefault(); execCmd("italic"); }}
        >
          <i>I</i>
        </button>
        <button
          type="button"
          className="html-editor__btn"
          title="Underline"
          onMouseDown={(e) => { e.preventDefault(); execCmd("underline"); }}
        >
          <u>U</u>
        </button>
        <button
          type="button"
          className="html-editor__btn"
          title="Bulleted list"
          onMouseDown={(e) => { e.preventDefault(); execCmd("insertUnorderedList"); }}
        >
          •
        </button>
        <button
          type="button"
          className="html-editor__btn"
          title="Numbered list"
          onMouseDown={(e) => { e.preventDefault(); execCmd("insertOrderedList"); }}
        >
          1.
        </button>
        <span className="html-editor__divider" />
        {TEMPLATE_VARS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            className="html-editor__var-btn"
            title={`Insert {{${key}}}`}
            onMouseDown={(e) => { e.preventDefault(); insertVar(key); }}
          >
            {`{{${label}}}`}
          </button>
        ))}
      </div>
      <div
        ref={ref}
        className="html-editor__content"
        contentEditable
        suppressContentEditableWarning
        data-placeholder="Compose your message here. Use the toolbar to format text or insert per-store template variables."
        onCompositionStart={() => { isComposing.current = true; }}
        onCompositionEnd={() => { isComposing.current = false; push(); }}
        onInput={() => { if (!isComposing.current) push(); }}
        onBlur={push}
      />
    </div>
  );
}

// ── Tags input ───────────────────────────────────────────────────────────────

function TagsInput({
  tags,
  onChange,
}: {
  tags: string[];
  onChange: (tags: string[]) => void;
}) {
  const [input, setInput] = useState("");

  function commit() {
    const trimmed = input.trim();
    if (trimmed && !tags.includes(trimmed)) onChange([...tags, trimmed]);
    setInput("");
  }

  return (
    <div className="tags-input">
      <div className="tags-input__chips">
        {tags.map((tag) => (
          <span key={tag} className="tag">
            {tag}
            <button
              type="button"
              className="tags-input__remove"
              onClick={() => onChange(tags.filter((t) => t !== tag))}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <input
        className="tags-input__field"
        type="text"
        placeholder="Add tag and press Enter"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); commit(); }
          if (e.key === "Backspace" && !input && tags.length) {
            onChange(tags.slice(0, -1));
          }
        }}
        onBlur={commit}
      />
    </div>
  );
}

// ── Collapsible section ──────────────────────────────────────────────────────

function Accordion({
  title,
  badge,
  children,
  defaultOpen = false,
}: {
  title: string;
  badge?: number;
  children: ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`accordion${open ? " accordion--open" : ""}`}>
      <button
        type="button"
        className="accordion__header"
        onClick={() => setOpen((o) => !o)}
      >
        <span>{title}</span>
        <span className="accordion__right">
          {badge !== undefined && badge > 0 && (
            <span className="accordion__badge">{badge}</span>
          )}
          <span className="accordion__chevron">{open ? "▲" : "▼"}</span>
        </span>
      </button>
      {open && <div className="accordion__body">{children}</div>}
    </div>
  );
}

// ── Default compose state ────────────────────────────────────────────────────

interface ComposeState {
  messageType: MessageType;
  subject: string;
  teaser: string;
  body: string;
  templateLayout: TemplateLayout;
  senderName: string;
  tags: string[];
  sendMode: "now" | "scheduled";
  scheduledFor: string;
  isRecurring: boolean;
  recurrencePattern: string;
  isSecure: boolean;
  confidentialOwnersOnly: boolean;
  includeSignature: boolean;
  attachToOpsManual: boolean;
  attachmentMode: "shared" | "per-store";
}

const defaultCompose: ComposeState = {
  messageType: "general",
  subject: "",
  teaser: "",
  body: "",
  templateLayout: "free_form",
  senderName: "",
  tags: [],
  sendMode: "now",
  scheduledFor: "",
  isRecurring: false,
  recurrencePattern: "",
  isSecure: false,
  confidentialOwnersOnly: false,
  includeSignature: false,
  attachToOpsManual: false,
  attachmentMode: "shared",
};

const MESSAGE_TYPE_META: Record<MessageType, { label: string; color: string; desc: string }> = {
  general: { label: "General", color: "#2e7df6", desc: "Standard operational communication" },
  important: { label: "Important", color: "#e07a00", desc: "Requires prompt attention from stores" },
  emergency: { label: "Emergency", color: "#e10600", desc: "Critical — urgent action required" },
};

const LAYOUT_OPTIONS: { value: TemplateLayout; label: string }[] = [
  { value: "free_form", label: "Free form" },
  { value: "image_only", label: "Image only" },
  { value: "text_and_images", label: "Text + Images" },
  { value: "image_and_text", label: "Image + Text" },
  { value: "text_and_video", label: "Text + Video" },
];

// ── Page component ───────────────────────────────────────────────────────────

export default function CommunicationsPage() {
  // Store data — loaded from API, fallback to mock
  const [stores, setStores] = useState<StoreRecord[]>([]);
  const [apiStores, setApiStores] = useState<StoreEmailDto[]>([]);
  const [storesLoading, setStoresLoading] = useState(true);

  // Audience selection
  const [selections, setSelections] = useState<AudienceSelections>({
    countries: [],
    regions: [],
    ownershipTypes: [],
    franchiseGroups: [],
  });
  const [manualIncludes, setManualIncludes] = useState<string[]>([]);
  const [manualExcludes, setManualExcludes] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Compose
  const [compose, setCompose] = useState<ComposeState>(defaultCompose);

  // Attachments — shared files
  const [sharedAttachments, setSharedAttachments] = useState<File[]>([]);
  // Per-store attachments: storeNumber → File
  const [perStoreFiles, setPerStoreFiles] = useState<Record<string, File>>({});

  // Draft state
  const [draftId, setDraftId] = useState<string | null>(null);

  // Submit state
  const [submitState, setSubmitState] = useState<{
    status: "idle" | "submitting" | "success" | "error";
    message: string;
  }>({ status: "idle", message: "" });

  // Preview modal
  const [showPreview, setShowPreview] = useState(false);

  // ── Load stores from API ──────────────────────────────────────────────────

  const loadStores = useCallback(async () => {
    setStoresLoading(true);
    try {
      const data = await fetchStoreGroups();
      setApiStores(data.all);
      setStores(apiStoresToRecords(data.all, data.byRegion));
    } catch {
      // Fallback to mock data when API is not configured
      setStores(mockStores);
    } finally {
      setStoresLoading(false);
    }
  }, []);

  useEffect(() => { loadStores(); }, [loadStores]);

  // ── Derived audience ──────────────────────────────────────────────────────

  const countries = uniqueCountries(stores);
  const regions = uniqueRegions(stores);
  const franchises = uniqueFranchiseGroups(stores);
  const ownershipTypes = ownershipOptions(stores);

  const baseSelected = collectAudienceStoreIds(stores, selections);
  const finalSelected = new Set<string>([...baseSelected, ...manualIncludes]);
  for (const id of manualExcludes) finalSelected.delete(id);

  const selectedStores = stores.filter((s) => finalSelected.has(s.id));
  const selectedEmails = uniqueRecipientEmails(selectedStores);
  const missingEmail = selectedStores.filter((s) => s.emails.length === 0);

  const visibleStores = [...stores]
    .filter((s) => storeMatchesSearch(s, searchTerm))
    .sort((a, b) => {
      const aS = finalSelected.has(a.id) ? 1 : 0;
      const bS = finalSelected.has(b.id) ? 1 : 0;
      if (aS !== bS) return bS - aS;
      return a.displayName.localeCompare(b.displayName);
    });

  // ── Helpers ───────────────────────────────────────────────────────────────

  function clearOverrides() {
    setManualIncludes([]);
    setManualExcludes([]);
  }

  function quickSelect(kind: "all" | OwnershipType | "clear") {
    clearOverrides();
    if (kind === "clear") {
      setSelections({ countries: [], regions: [], ownershipTypes: [], franchiseGroups: [] });
    } else if (kind === "all") {
      setSelections({ countries, regions: [], ownershipTypes: [], franchiseGroups: [] });
    } else {
      setSelections({ countries: [], regions: [], ownershipTypes: [kind], franchiseGroups: [] });
    }
  }

  function selectCountry(c: "US" | "CA") {
    clearOverrides();
    setSelections({ countries: [c], regions: [], ownershipTypes: [], franchiseGroups: [] });
  }

  function toggleStore(id: string) {
    const isSelected = finalSelected.has(id);
    const isBase = baseSelected.has(id);
    if (isSelected) {
      if (isBase) {
        setManualExcludes((prev) => prev.includes(id) ? prev : [...prev, id]);
      } else {
        setManualIncludes((prev) => prev.filter((x) => x !== id));
      }
    } else {
      if (isBase) {
        setManualExcludes((prev) => prev.filter((x) => x !== id));
      } else {
        setManualIncludes((prev) => prev.includes(id) ? prev : [...prev, id]);
      }
    }
  }

  function set<K extends keyof ComposeState>(key: K, value: ComposeState[K]) {
    setCompose((prev) => ({ ...prev, [key]: value }));
  }

  // ── Build DTO ─────────────────────────────────────────────────────────────

  function buildDto(): CreateCampaignDto {
    const storeNumbers = selectedStores.map((s) => s.storeNumber);
    const dto: CreateCampaignDto = {
      messageType: compose.messageType,
      subject: compose.subject,
      body: compose.body,
      ...(compose.teaser ? { teaser: compose.teaser } : {}),
      templateLayout: compose.templateLayout,
      targetAll: finalSelected.size === stores.length && stores.length > 0,
      storeNumbers,
      ...(compose.senderName ? { senderName: compose.senderName } : {}),
      ...(compose.tags.length ? { tags: compose.tags } : {}),
      ...(compose.isSecure ? { isSecure: true } : {}),
      ...(compose.confidentialOwnersOnly ? { confidentialOwnersOnly: true } : {}),
      ...(compose.includeSignature ? { includeSignature: true } : {}),
      ...(compose.attachToOpsManual ? { attachToOpsManual: true } : {}),
      ...(compose.sendMode === "scheduled" && compose.scheduledFor
        ? { scheduledAt: new Date(compose.scheduledFor).toISOString() }
        : {}),
      ...(compose.isRecurring && compose.recurrencePattern
        ? { isRecurring: true, recurrencePattern: compose.recurrencePattern }
        : {}),
    };
    return dto;
  }

  // ── Validation ────────────────────────────────────────────────────────────

  function validate() {
    if (selectedStores.length === 0) return "Select at least one store.";
    if (!compose.subject.trim()) return "Subject is required.";
    if (!compose.body.trim() || compose.body === "<br>") return "Body is required.";
    if (
      compose.sendMode === "scheduled" &&
      (!compose.scheduledFor || Number.isNaN(Date.parse(compose.scheduledFor)))
    ) return "Choose a valid future date and time.";
    if (
      compose.sendMode === "scheduled" &&
      new Date(compose.scheduledFor).getTime() <= Date.now()
    ) return "Scheduled time must be in the future.";
    if (compose.isRecurring && !compose.recurrencePattern.trim())
      return "Recurrence pattern is required when recurring is enabled.";
    return null;
  }

  // ── Upload per-store attachments and collect blobPaths ────────────────────

  async function uploadPerStoreAttachments(): Promise<Record<string, string>> {
    const blobPaths: Record<string, string> = {};
    await Promise.all(
      Object.entries(perStoreFiles).map(async ([storeNum, file]) => {
        try {
          const blobPath = await uploadStoreAttachment(storeNum, file);
          blobPaths[storeNum] = blobPath;
        } catch {
          // Non-fatal — skip this store's attachment
        }
      }),
    );
    return blobPaths;
  }

  async function uploadSharedAttachment(): Promise<Record<string, string>> {
    const file = sharedAttachments[0];

    if (!file) {
      return {};
    }

    const uploads = await Promise.all(
      selectedStores.map(async (store) => [
        store.storeNumber,
        await uploadStoreAttachment(store.storeNumber, file),
      ] as const),
    );

    return Object.fromEntries(uploads);
  }

  // ── Actions ───────────────────────────────────────────────────────────────

  async function handleSaveDraft() {
    setSubmitState({ status: "submitting", message: "Saving draft…" });
    try {
      const dto = buildDto();
      const result = draftId
        ? await updateDraft(draftId, dto)
        : await saveDraft(dto);
      setDraftId(result.campaignId);
      setSubmitState({ status: "success", message: `Draft saved (ID: ${result.campaignId}).` });
    } catch (err) {
      setSubmitState({
        status: "error",
        message: err instanceof Error ? err.message : "Failed to save draft.",
      });
    }
  }

  async function handleSendDraft() {
    if (!draftId) { handleSubmit(); return; }
    setSubmitState({ status: "submitting", message: "Sending draft…" });
    try {
      const result = await sendDraft(draftId);
      setSubmitState({
        status: "success",
        message: `Campaign sent! ID: ${result.campaignId}. Targeting ${result.totalCount} stores.`,
      });
    } catch (err) {
      setSubmitState({
        status: "error",
        message: err instanceof Error ? err.message : "Failed to send draft.",
      });
    }
  }

  async function handleSubmit(e?: FormEvent) {
    e?.preventDefault();
    const err = validate();
    if (err) { setSubmitState({ status: "error", message: err }); return; }

    setSubmitState({ status: "submitting", message: "Uploading attachments and queueing campaign…" });
    try {
      const dto = buildDto();

      if (compose.attachmentMode === "per-store" && Object.keys(perStoreFiles).length > 0) {
        dto.attachments = await uploadPerStoreAttachments();
      }
      if (compose.attachmentMode === "shared" && sharedAttachments.length > 0) {
        dto.attachments = await uploadSharedAttachment();
      }

      const result = await createCampaign(dto);
      setSubmitState({
        status: "success",
        message: `Campaign queued! ID: ${result.campaignId}. Targeting ${result.totalCount} stores.`,
      });
      setDraftId(null);
    } catch (err) {
      setSubmitState({
        status: "error",
        message: err instanceof Error ? err.message : "Failed to queue campaign.",
      });
    }
  }

  // ── Attachment helpers ────────────────────────────────────────────────────

  function addSharedFiles(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setSharedAttachments([file]);
    e.target.value = "";
  }

  function addPerStoreFile(storeNum: string, e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setPerStoreFiles((prev) => ({ ...prev, [storeNum]: file }));
    e.target.value = "";
  }

  const msgMeta = MESSAGE_TYPE_META[compose.messageType];

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <AppShell
      title="Store communications workspace"
      eyebrow="Send communication"
      description="Select your audience, compose the message, configure delivery options, then save as draft or send immediately."
      showBackLink
    >
      {/* ── Stats bar ───────────────────────────────────────────────────── */}
      <section className="section">
        <div className="stats-grid">
          <article className="stat-card">
            <span>Selected stores</span>
            <strong>{selectedStores.length}</strong>
            <p>{storesLoading ? "Loading stores…" : `of ${stores.length} total`}</p>
          </article>
          <article className="stat-card">
            <span>Recipient emails</span>
            <strong>{selectedEmails.length}</strong>
            <p>Unique addresses from selected stores</p>
          </article>
          <article className="stat-card">
            <span>Missing email</span>
            <strong className={missingEmail.length > 0 ? "stat-card__warn" : ""}>
              {missingEmail.length}
            </strong>
            <p>Stores without a deliverable address</p>
          </article>
          <article className="stat-card">
            <span>Attachments</span>
            <strong>
              {compose.attachmentMode === "shared"
                ? sharedAttachments.length
                : Object.keys(perStoreFiles).length}
            </strong>
            <p>{compose.attachmentMode === "shared" ? "Shared files" : "Per-store files"}</p>
          </article>
        </div>
      </section>

      {/* ── Main two-panel grid ──────────────────────────────────────────── */}
      <div className="section comms-grid">

        {/* ── LEFT: Audience builder ─────────────────────────────────────── */}
        <article className="panel">
          <div className="panel__header">
            <div>
              <p className="section__eyebrow">Audience builder</p>
              <h2>Target by category, then refine by store.</h2>
            </div>
            <p className="panel__hint">
              Union of all checked categories + manual overrides.
            </p>
          </div>

          {/* Quick actions */}
          <div className="quick-actions">
            <button className="button button--ghost" type="button" onClick={() => quickSelect("all")}>
              All North America
            </button>
            <button className="button button--ghost" type="button" onClick={() => selectCountry("US")}>
              U.S. only
            </button>
            <button className="button button--ghost" type="button" onClick={() => selectCountry("CA")}>
              Canada only
            </button>
            <button className="button button--ghost" type="button" onClick={() => quickSelect("Corporate")}>
              Corporate-owned
            </button>
            <button className="button button--ghost" type="button" onClick={() => quickSelect("Franchise")}>
              Franchise-owned
            </button>
            <button className="button button--text" type="button" onClick={() => quickSelect("clear")}>
              Clear selection
            </button>
          </div>

          {/* Group accordions */}
          <div className="audience-accordions">
            <Accordion
              title="By Region"
              badge={selections.regions.length}
              defaultOpen={regions.length > 0}
            >
              <div className="check-stack">
                {regions.map((r) => (
                  <label key={r} className="check-row">
                    <input
                      type="checkbox"
                      checked={selections.regions.includes(r)}
                      onChange={() =>
                        setSelections((prev) => ({
                          ...prev,
                          regions: toggleValue(prev.regions, r),
                        }))
                      }
                    />
                    <span>{r}</span>
                  </label>
                ))}
              </div>
            </Accordion>

            <Accordion
              title="By Ownership"
              badge={selections.ownershipTypes.length}
            >
              <div className="check-stack">
                {ownershipTypes.map((o) => (
                  <label key={o} className="check-row">
                    <input
                      type="checkbox"
                      checked={selections.ownershipTypes.includes(o)}
                      onChange={() =>
                        setSelections((prev) => ({
                          ...prev,
                          ownershipTypes: toggleValue(prev.ownershipTypes, o) as OwnershipType[],
                        }))
                      }
                    />
                    <span>{o}</span>
                  </label>
                ))}
              </div>
            </Accordion>

            <Accordion
              title="By Franchise Group"
              badge={selections.franchiseGroups.length}
            >
              <div className="check-stack">
                {franchises.map((f) => (
                  <label key={f} className="check-row">
                    <input
                      type="checkbox"
                      checked={selections.franchiseGroups.includes(f)}
                      onChange={() =>
                        setSelections((prev) => ({
                          ...prev,
                          franchiseGroups: toggleValue(prev.franchiseGroups, f),
                        }))
                      }
                    />
                    <span>{f}</span>
                  </label>
                ))}
              </div>
            </Accordion>

            <Accordion title="By Country" badge={selections.countries.length}>
              <div className="check-stack">
                {countries.map((c) => (
                  <label key={c} className="check-row">
                    <input
                      type="checkbox"
                      checked={selections.countries.includes(c)}
                      onChange={() =>
                        setSelections((prev) => ({
                          ...prev,
                          countries: toggleValue(prev.countries, c) as AudienceSelections["countries"],
                        }))
                      }
                    />
                    <span>{c === "US" ? "United States" : "Canada"}</span>
                  </label>
                ))}
              </div>
            </Accordion>
          </div>

          {/* Store browser */}
          <div className="store-browser">
            <div className="store-browser__header">
              <div>
                <h3>Individual stores</h3>
                <p>Check or uncheck to override the category selections above.</p>
              </div>
              <input
                className="search-input"
                type="search"
                placeholder="Search by store, city, region, email…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="store-list">
              {storesLoading && (
                <div className="store-list__loading">Loading stores…</div>
              )}
              {!storesLoading && visibleStores.length === 0 && (
                <div className="store-list__empty">No stores match your search.</div>
              )}
              {visibleStores.map((store) => {
                const isSelected = finalSelected.has(store.id);
                return (
                  <label
                    key={store.id}
                    className={`store-card${isSelected ? " store-card--selected" : ""}`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleStore(store.id)}
                    />
                    <div className="store-card__content">
                      <div className="store-card__heading">
                        <strong>
                          {store.displayName} <span>#{store.storeNumber}</span>
                        </strong>
                        <span className="store-card__location">
                          {store.city}{store.state ? `, ${store.state}` : ""}
                        </span>
                      </div>
                      <div className="tag-row">
                        <span className="tag">{store.country}</span>
                        <span className="tag">{store.region}</span>
                        <span className="tag">{store.ownershipType}</span>
                        {store.franchiseGroup && (
                          <span className="tag">{store.franchiseGroup}</span>
                        )}
                        {store.emails.length === 0 && (
                          <span className="tag tag--warning">No email</span>
                        )}
                      </div>
                      <p className="store-card__emails">
                        {store.emails.length > 0 ? store.emails.join(", ") : "No deliverable address"}
                      </p>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        </article>

        {/* ── RIGHT: Compose + options ────────────────────────────────────── */}
        <article className="panel">
          <div className="panel__header">
            <div>
              <p className="section__eyebrow">Message composer</p>
              <h2>Write your communication and configure delivery.</h2>
            </div>
          </div>

          <form className="composer" onSubmit={handleSubmit}>

            {/* Message type */}
            <div className="field">
              <span>Message type</span>
              <div className="msg-type-row">
                {(["general", "important", "emergency"] as MessageType[]).map((t) => (
                  <button
                    key={t}
                    type="button"
                    className={`msg-type-btn${compose.messageType === t ? " msg-type-btn--active" : ""}`}
                    style={{
                      "--msg-color": MESSAGE_TYPE_META[t].color,
                    } as CSSProperties}
                    onClick={() => set("messageType", t)}
                  >
                    {MESSAGE_TYPE_META[t].label}
                  </button>
                ))}
              </div>
              <p className="field__hint" style={{ color: msgMeta.color }}>
                {msgMeta.desc}
              </p>
            </div>

            <div
              className="msg-type-banner"
              style={{ background: msgMeta.color }}
            >
              {compose.messageType.toUpperCase()} MESSAGE
            </div>

            {/* Core fields */}
            <label className="field">
              <span>Subject *</span>
              <input
                type="text"
                placeholder="Action required: update your weekend hours"
                value={compose.subject}
                onChange={(e) => set("subject", e.target.value)}
              />
            </label>

            <label className="field">
              <span>Preview text (teaser)</span>
              <input
                type="text"
                placeholder="Optional email preview snippet shown in inbox"
                value={compose.teaser}
                onChange={(e) => set("teaser", e.target.value)}
              />
            </label>

            <div className="field">
              <span>Body *</span>
              <HtmlEditor value={compose.body} onChange={(v) => set("body", v)} />
            </div>

            <label className="field">
              <span>Template layout</span>
              <select
                value={compose.templateLayout}
                onChange={(e) => set("templateLayout", e.target.value as TemplateLayout)}
              >
                {LAYOUT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>

            {/* Delivery options accordion */}
            <Accordion title="Delivery options" defaultOpen>
              <div className="field-grid">
                <label className="field">
                  <span>Send mode</span>
                  <select
                    value={compose.sendMode}
                    onChange={(e) => set("sendMode", e.target.value as "now" | "scheduled")}
                  >
                    <option value="now">Send immediately</option>
                    <option value="scheduled">Schedule for later</option>
                  </select>
                </label>
                <label className="field">
                  <span>Scheduled date &amp; time</span>
                  <input
                    type="datetime-local"
                    value={compose.scheduledFor}
                    onChange={(e) => set("scheduledFor", e.target.value)}
                    disabled={compose.sendMode !== "scheduled"}
                  />
                </label>
              </div>

              <label className="check-row" style={{ marginTop: "0.75rem" }}>
                <input
                  type="checkbox"
                  checked={compose.isRecurring}
                  onChange={(e) => set("isRecurring", e.target.checked)}
                />
                <span>Recurring campaign</span>
              </label>

              {compose.isRecurring && (
                <label className="field" style={{ marginTop: "0.75rem" }}>
                  <span>Recurrence pattern (cron or description)</span>
                  <input
                    type="text"
                    placeholder="e.g. 0 9 * * MON (every Monday 9am)"
                    value={compose.recurrencePattern}
                    onChange={(e) => set("recurrencePattern", e.target.value)}
                  />
                </label>
              )}
            </Accordion>

            {/* Security & options accordion */}
            <Accordion title="Security &amp; options">
              <div className="check-stack">
                <label className="check-row">
                  <input
                    type="checkbox"
                    checked={compose.isSecure}
                    onChange={(e) => set("isSecure", e.target.checked)}
                  />
                  <span>Secure communication</span>
                </label>
                <label className="check-row">
                  <input
                    type="checkbox"
                    checked={compose.confidentialOwnersOnly}
                    onChange={(e) => set("confidentialOwnersOnly", e.target.checked)}
                  />
                  <span>Confidential — owners only</span>
                </label>
                <label className="check-row">
                  <input
                    type="checkbox"
                    checked={compose.includeSignature}
                    onChange={(e) => set("includeSignature", e.target.checked)}
                  />
                  <span>Include corporate signature</span>
                </label>
                <label className="check-row">
                  <input
                    type="checkbox"
                    checked={compose.attachToOpsManual}
                    onChange={(e) => set("attachToOpsManual", e.target.checked)}
                  />
                  <span>Attach to operations manual</span>
                </label>
              </div>

              <div className="field-grid" style={{ marginTop: "1rem" }}>
                <label className="field">
                  <span>Sender name override</span>
                  <input
                    type="text"
                    placeholder="e.g. Edible Corporate Team"
                    value={compose.senderName}
                    onChange={(e) => set("senderName", e.target.value)}
                  />
                </label>
                <div className="field">
                  <span>Tags</span>
                  <TagsInput tags={compose.tags} onChange={(t) => set("tags", t)} />
                </div>
              </div>
            </Accordion>

            {/* Attachments accordion */}
            <Accordion title="Attachments">
              <div className="field">
                <span>Attachment mode</span>
                <div className="check-stack check-stack--inline">
                  <label className="check-row">
                    <input
                      type="radio"
                      name="attachMode"
                      value="shared"
                      checked={compose.attachmentMode === "shared"}
                      onChange={() => set("attachmentMode", "shared")}
                    />
                    <span>Shared (same file for all stores)</span>
                  </label>
                  <label className="check-row">
                    <input
                      type="radio"
                      name="attachMode"
                      value="per-store"
                      checked={compose.attachmentMode === "per-store"}
                      onChange={() => set("attachmentMode", "per-store")}
                    />
                    <span>Per-store (unique file per store)</span>
                  </label>
                </div>
              </div>

              {compose.attachmentMode === "shared" && (
                <>
                  <label className="field" style={{ marginTop: "0.75rem" }}>
                    <span>Attach one shared file</span>
                    <input type="file" onChange={addSharedFiles} />
                  </label>
                  <div className="attachment-list">
                    {sharedAttachments.map((f) => (
                      <div key={f.name} className="attachment-chip">
                        <div>
                          <strong>{f.name}</strong>
                          <span>{formatFileSize(f.size)}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setSharedAttachments((p) => p.filter((x) => x.name !== f.name))}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {compose.attachmentMode === "per-store" && (
                <div className="per-store-attachments">
                  <p className="panel__hint" style={{ marginTop: "0.5rem" }}>
                    Upload a unique PDF/file for each selected store. Files are uploaded directly to Azure Blob via a presigned URL.
                  </p>
                  {selectedStores.length === 0 && (
                    <p className="store-list__empty">Select stores first to upload per-store files.</p>
                  )}
                  {selectedStores.map((store) => (
                    <div key={store.id} className="per-store-row">
                      <span className="per-store-row__label">
                        <strong>#{store.storeNumber}</strong> {store.displayName}
                      </span>
                      {perStoreFiles[store.storeNumber] ? (
                        <div className="attachment-chip" style={{ flex: 1 }}>
                          <div>
                            <strong>{perStoreFiles[store.storeNumber].name}</strong>
                            <span>{formatFileSize(perStoreFiles[store.storeNumber].size)}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => setPerStoreFiles((p) => { const n = { ...p }; delete n[store.storeNumber]; return n; })}
                          >
                            Remove
                          </button>
                        </div>
                      ) : (
                        <label className="per-store-row__upload">
                          <input
                            type="file"
                            accept=".pdf,.doc,.docx,.xlsx,.png,.jpg"
                            onChange={(e) => addPerStoreFile(store.storeNumber, e)}
                          />
                          <span>Choose file</span>
                        </label>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Accordion>

            {/* Recipient summary */}
            <div className="recipient-summary">
              <h3>Recipient preview</h3>
              <p>
                {selectedEmails.length > 0
                  ? selectedEmails.slice(0, 12).join(", ") +
                    (selectedEmails.length > 12 ? ` + ${selectedEmails.length - 12} more` : "")
                  : "No deliverable recipient emails selected yet."}
              </p>
            </div>

            {/* Status banner */}
            {submitState.status !== "idle" && (
              <div
                className={`status-banner status-banner--${submitState.status === "error" ? "error" : submitState.status === "submitting" ? "info" : "ok"}`}
              >
                {submitState.message}
              </div>
            )}

            {/* Action row */}
            <div className="composer__actions">
              <button
                type="button"
                className="button button--ghost"
                onClick={() => setShowPreview(true)}
                disabled={!compose.subject || selectedStores.length === 0}
              >
                Preview
              </button>
              <button
                type="button"
                className="button button--ghost"
                onClick={handleSaveDraft}
                disabled={submitState.status === "submitting"}
              >
                {draftId ? "Update draft" : "Save draft"}
              </button>
              {draftId && (
                <button
                  type="button"
                  className="button button--ghost"
                  onClick={handleSendDraft}
                  disabled={submitState.status === "submitting"}
                >
                  Send draft
                </button>
              )}
              <button
                type="submit"
                className="button button--primary"
                disabled={submitState.status === "submitting"}
              >
                {submitState.status === "submitting" ? "Sending…" : "Send now"}
              </button>
            </div>
          </form>
        </article>
      </div>

      {/* Preview modal */}
      {showPreview && (
        <PreviewModal
          campaign={buildDto()}
          stores={apiStores}
          onClose={() => setShowPreview(false)}
        />
      )}
    </AppShell>
  );
}
